/**
 * frontend/src/components/valuation/ValuationStepGuard.tsx
 *
 * VAL-PATH-ROUTES (P1) — wraps every engagement route. It sends a person who
 * opened a step that is not theirs (owner on Methodology or Review), a locked
 * step (D3), or the retired /overview to the right step, and hands the role
 * and engagement down to the step bar so it marks the same steps the left
 * nav does. The decision itself is guardRedirect() in _steps.ts.
 *
 * VAL-PATH-FOOTER (P2a): a held step shows a skeleton, not a blank page (U6),
 * and the step's footer and the step bar share one mover (StepMoveProvider).
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { getValuationEngagement } from '@/api/valuation'
import type { ValuationEngagement } from '@/types/valuation'
import { guardRedirect, stepRole, type GuardStep } from '@/pages/valuation/_steps'
import { StepCtx, StepMoveProvider } from '@/pages/valuation/_stepContext'
import { Skeleton } from '@/components/ui/skeleton'

// Owner screens whose answer depends on the engagement: hold them until it loads.
const OWNER_WAITS: readonly GuardStep[] = ['adjust', 'intake', 'output', 'signoff', 'review', 'overview']

export function ValuationStepGuard({ step, children }: { step: GuardStep; children: ReactNode }) {
  const { valuationId = '' } = useParams<{ valuationId: string }>()
  const { pathname } = useLocation()
  const { user } = useAuth()
  const role = user?.role
  const [engagement, setEngagement] = useState<ValuationEngagement | null>(null)
  // The route the last read finished for. React keeps this guard across steps,
  // so a read from the previous screen (before its save) is stale here and must
  // not decide a redirect (FIX-SIGNER-PREFILL-AND-FIRST-CLICK bug 2).
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  const loaded = loadedFor === pathname

  // Re-read on every route change so a step saved on one screen is done on the next.
  useEffect(() => {
    if (!valuationId) return
    let alive = true
    getValuationEngagement(valuationId)
      .then(e => { if (alive) setEngagement(e) })
      .catch(() => { /* the screen shows its own not-found */ })
      .finally(() => { if (alive) setLoadedFor(pathname) })
    return () => { alive = false }
  }, [valuationId, pathname])

  const waits = stepRole(role) === 'owner' && OWNER_WAITS.includes(step)
  const early = guardRedirect(step, role, null, valuationId)
  if (early) return <Navigate to={early} replace />
  if (waits && !loaded) {
    return (
      <div role="status" aria-label="Loading" className="space-y-4">
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-10 w-40" />
      </div>
    )
  }

  const to = guardRedirect(step, role, loaded ? engagement : null, valuationId)
  if (to) return <Navigate to={to} replace />
  if (step === 'overview') return null
  return (
    <StepCtx.Provider value={{ role, engagement }}>
      <StepMoveProvider>{children}</StepMoveProvider>
    </StepCtx.Provider>
  )
}
